<script>
import groupBy from 'lodash/groupBy'
import BlockContent from '../UI/Content/BlockContent.vue'
import EntityModal from './EntityModal.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalMethodsMixin, globalVariablesMixin } from '../../Mixins/global'
import Scoring from './Scoring.vue'
import Badge from '../UI/Badge.vue'
import EntityAddressList from './EntityAddressList.vue'
import EntityAddressFormDialog from './EntityAddressFormDialog.vue'
import PlaceholderContent from '../UI/Loading/PlaceholderContent.vue'
import useEntityApi from '../../../../Composables/useEntityApi.ts'
import EntityBillingCodeList from './EntityBillingCodeList.vue'
import EntityBillingCodeFormDialog from './EntityBillingCodeFormDialog.vue'
import ButtonAllNotReadonly from '../UI/Button/ButtonAllNotReadonly.vue'

export default {
  components: {
    EntityBillingCodeFormDialog,
    EntityBillingCodeList,
    PlaceholderContent,
    EntityAddressFormDialog,
    ButtonAllNotReadonly,
    EntityAddressList,
    Badge,
    Scoring,
    EntityModal,
    BlockContent,
  },
  mixins: [mixinComponent, globalVariablesMixin, globalMethodsMixin],

  props: {
    entityId: { type: Number, required: true },
  },

  data() {
    return {
      entity: {},
      contracts: {},
      users: {},
      iconCS: '',
      iconEli: '',
      loading: null,
      scores: null,
      providers: null,
      displayEntityAddressForm: false,
      displayEntityBillingCodeForm: false,
      addressToEdit: null,
      billingCodeToEdit: null,
    }
  },

  computed: {
    entitySirenLabel() {
      if (this.entity?.countryCode === 'ES') {
        return this.$t('entity.nif.title')
      }

      return this.$t('entity.siren')
    },
    scoresGroupedByProvider() {
      return groupBy(this.scores, 'provider')
    },
  },

  methods: {
    formatScore(score) {
      let maxScore = 100
      if (score.type === 'informa') {
        maxScore = 20
      } else if (score.provider === 'Informa' && score.type === 'liquidation_score') {
        maxScore = 100
      } else if (score.provider === 'Ellisphere') {
        maxScore = 10
      } else {
        maxScore = score.score >= 10 ? 100 : 10 // TODO: Use score.type when it will be configured for other providers
      }

      return `${score.score}/${maxScore}`
    },
    scoreComment({ provider, type, description }) {
      if (provider !== 'Informa') {
        return description
      }

      return this.$t(`score.type.${type}`)
    },
    sortScores() {
      this.scores = this.entity.scores.sort((a, b) => {
        return new Date(b.scoreAt) - new Date(a.scoreAt)
      })
    },
    arrayProvider() {
      let providers = []
      this.entity.scores.map((score) => {
        providers.push(score.provider)
      })

      this.providers = providers.sort().filter((item, pos, ary) => {
        return !pos || item != ary[pos - 1]
      })
    },
    arrayContact() {
      let contacts = []
      let usersSorted = this.entity.userEntitiesSorted
      for (const index of usersSorted) {
        contacts.push(index.user)
      }

      this.users = contacts
    },
    scrollComment(scoreInfos) {
      let size = 0
      if (scoreInfos.creditsafe.comment) {
        size += scoreInfos.creditsafe.comment.length
      }

      if (scoreInfos.ellisphere.comment) {
        size += scoreInfos.ellisphere.comment.length
      }

      if (size > 300) {
        return 'scrollbar-y '
      }

      return ''
    },
    isSigner(email) {
      for (const index in this.contracts) {
        if (this.contracts[index].buyer) {
          if (this.contracts[index].buyer.email == email) {
            return true
          }
        }
      }

      return false
    },
    showModal() {
      this.$refs.modalsociete.showForm()
    },
    async getEntity() {
      this.loading = true

      try {
        ;[this.entity, this.contracts] = await Promise.all([this.fetchEntity(), this.fetchContracts()])

        this.sortScores()
        this.arrayProvider()
        this.arrayContact()
      } finally {
        this.loading = false
      }
    },
    onEntityChanged() {
      this.getEntity()
    },
    showEntityAddressForm(addressToUpdate = null) {
      this.addressToEdit = addressToUpdate
      this.displayEntityAddressForm = true
    },
    cancelAddressEdition() {
      this.addressToEdit = null
      this.displayEntityAddressForm = false
    },
    onAddressChange({ address }) {
      this.addressToEdit = null
      this.displayEntityAddressForm = false

      const existingAddressIndex = this.entity.addresses.findIndex(({ id }) => id === address.id)
      if (existingAddressIndex >= 0) {
        this.entity.addresses[existingAddressIndex] = address
      } else {
        this.entity.addresses.push(address)
      }
    },
    showEntityBillingForm(billingCodeToUpdate = null) {
      this.billingCodeToEdit = billingCodeToUpdate
      this.displayEntityBillingCodeForm = true
    },
    cancelBillingCodeEdition() {
      this.billingCodeToEdit = null
      this.displayEntityBillingCodeForm = false
    },
    onBillingCodeChange({ billingCode }) {
      this.billingCodeToEdit = null
      this.displayEntityBillingCodeForm = false

      const existingBillingCodeIndex = this.entity.billingCodes.findIndex(({ id }) => id === billingCode.id)
      if (existingBillingCodeIndex >= 0) {
        this.entity.billingCodes[existingBillingCodeIndex] = billingCode
      } else {
        this.entity.billingCodes.push(billingCode)
      }

      this.getEntity()
    },
    getCreditLimit(provider) {
      switch (provider) {
        case 'Credit Safe':
          return Number(this.entity.creditLimitCS)
        case 'Ellisphere':
          return Number(this.entity.creditLimitEli)
        default:
          return null
      }
    },
    formatName({ name, firstname, lastname }) {
      if (!!name) {
        return name
      }

      return [firstname, lastname].filter((value) => !!value).join(' ')
    },
  },

  setup(props) {
    const { fetchEntity, fetchContracts } = useEntityApi(props.entityId)

    return { fetchEntity, fetchContracts }
  },
  beforeMount() {
    this.getEntity()
  },
}
</script>
<template>
  <div id="document-entity">
    <div v-if="!loading">
      <div class="row">
        <div class="col-12">
          <BlockContent inner-bloc-class="border-contract-yellow">
            <template #header-title>
              <div class="d-flex justify-content-between">
                <h5 class="text-primary" v-html="$t('entity.information.presentation.title')"></h5>

                <div class="ml-2">
                  <EntityModal
                    v-if="!user.isReadOnly && hasRole('ROLE_ADVANCE_ENTITY_CREATE')"
                    :add="false"
                    :id="entityId"
                    :context="entityId"
                    @submitted="onEntityChanged"
                  />
                </div>
              </div>
            </template>
            <div class="row">
              <div class="col-md-6">
                <div class="row">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.name') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityName">{{ entity.name }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.address') }}</b>
                  </div>
                  <div
                    class="col-md-7"
                    v-html="entity.address ? entity.address.formattedHtml : ''"
                    e2e-data="entityAddress"
                  ></div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ entitySirenLabel }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entitySiren">{{ entity.siren }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.rcs') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityRcs">{{ entity.rcs }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.vatIntra') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityVatIntra">{{ entity.vatIntra }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.createdAt') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityCreatedAt">{{ setDate(entity.createdAt) }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.fields.disputeType.label') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityDisputeType">
                    {{ entity.disputeType ? entity.disputeType.name : '-' }}
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="row">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.capital') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityCapital">{{ $n(Number(entity.capital), 'currency') }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.legalForm') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityLegalForm">{{ entity.legalForm }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.activity') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityActivityDesc">{{ entity.activityDesc }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.workforce') }} </b>
                  </div>
                  <div class="col-md-7" e2e-data="entityWorkforce">{{ entity.workforce }}</div>
                </div>
                <div class="row" style="margin-top: 2rem !important">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.sector') }} </b>
                  </div>
                  <div class="col-md-7" e2e-data="entityActivityDescSector">{{ entity.activityDesc }}</div>
                </div>
                <div v-if="hasRole('ROLE_REPORTING_VARIABLE')" class="row mt-2">
                  <div class="col-md-5">
                    <b>{{ $t('entity.information.presentation.reportingVariables') }}</b>
                  </div>
                  <div class="col-md-7" e2e-data="entityReportingVariables">
                    <template v-if="entity.reportingVariables?.length > 0">
                      <Badge
                        v-for="variable in entity.reportingVariables"
                        :key="variable.id"
                        type="secondary"
                        class="mr-1"
                      >
                        {{ variable.name }}
                      </Badge>
                    </template>
                    <template v-else>-</template>
                  </div>
                </div>
              </div>
            </div>
          </BlockContent>
        </div>

        <div class="col-md-6">
          <!-- Partenaires -->
          <BlockContent color="primary" inner-bloc-class="border-contract-yellow h-100">
            <template #header>
              <h5 class="text-primary">
                {{ $t('entity.information.partner.title') }}
                <i :class="'fas fa-check-circle mr-2 ' + (entity.checkPlatform ? 'text-success' : 'text-danger')"></i>
              </h5>
            </template>
            <div class="row" v-for="companySalesman in entity.companySalesmen" :key="companySalesman.name">
              <div class="col-sm">
                <span class="title text-dark"
                  ><b>{{ $t('entity.information.partner.companySalesman') }}</b></span
                >
              </div>
              <div class="col-sm" e2e-data="companySalesmanName">{{ companySalesman.name }}</div>
            </div>
          </BlockContent>

          <!-- Liste des contacts -->
          <BlockContent
            title-t="entity.information.contactList.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            :bloc-content-class="users.length >= 5 ? 'scrollbar-y' : ''"
          >
            <template v-if="users && users.length > 0">
              <div class="row my-2" v-for="user in users">
                <div class="col-md-1">
                  <i :class="'fa fa-check-circle' + (user.enabled ? ' text-success' : 'text-danger')"></i>
                </div>
                <div class="col-md-8" e2e-data="contactName">
                  {{
                    user.name +
                    ' : ' +
                    user.email +
                    ' / ' +
                    (user.phone ? user.phone : $t('entity.information.contactList.phoneEmpty'))
                  }}
                </div>
                <div class="col-md-3">
                  <span v-if="isSigner(user.email)" class="user-sign">signataire</span>
                </div>
              </div>
            </template>
            <div v-else class="text-center">
              {{ $t('entity.information.contactList.empty') }}
            </div>
          </BlockContent>

          <!-- Liste des établissements -->
          <BlockContent title-level="5" color="primary" inner-bloc-class="border-contract-yellow">
            <template #header>
              <h5 class="text-primary">
                {{ $t('entity.information.addressList.title') }}
              </h5>
              <div>
                <ButtonAllNotReadonly
                  label="entity.action.addAddress"
                  margin="no"
                  type="add"
                  icon="add"
                  width="aligned"
                  e2e-button="addAddress"
                  @click="showEntityAddressForm(null)"
                />
              </div>
            </template>
            <EntityAddressList :addresses="entity.addresses" @edit="showEntityAddressForm($event.address)" />
            <EntityAddressFormDialog
              v-if="displayEntityAddressForm"
              :entity-id="entity.id"
              :address="addressToEdit"
              @close="cancelAddressEdition"
              @save="onAddressChange"
            />
          </BlockContent>

          <!-- Liste des codes d'adressage -->
          <BlockContent title-level="5" color="primary" inner-bloc-class="border-contract-yellow">
            <template #header>
              <h5 class="text-primary">
                {{ $t('entity.information.billingCodeList.title') }}
              </h5>
              <div>
                <ButtonAllNotReadonly
                  label="entity.action.addBillingCode"
                  margin="no"
                  type="add"
                  icon="add"
                  width="aligned"
                  @click="showEntityBillingForm(null)"
                />
              </div>
            </template>
            <EntityBillingCodeList
              :billing-codes="entity.billingCodes"
              @edit="showEntityBillingForm($event.billingCode)"
            />
            <EntityBillingCodeFormDialog
              v-if="displayEntityBillingCodeForm"
              :entity-id="entity.id"
              :billing-code="billingCodeToEdit"
              @close="cancelBillingCodeEdition"
              @save="onBillingCodeChange"
            />
          </BlockContent>

          <!-- Liste des directeurs -->
          <BlockContent
            v-if="entity.shareHolders"
            title-t="entity.information.directorList.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            bloc-content-class="scrollbar-x-mobile"
          >
            <table class="table table-striped table-hover fs-16 w-100">
              <thead>
                <tr>
                  <th class="font-weight-bold col-3">
                    {{ $t('entity.information.directorList.director') }}
                  </th>
                  <th class="font-weight-bold col-3">
                    {{ $t('entity.information.directorList.position') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.directorList.birthday') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.directorList.placeOfBirth') }}
                  </th>
                </tr>
              </thead>

              <tbody
                v-if="entity.shareHolders.length > 0"
                :class="entity.shareHolders.length > 3 ? 'scrollbar-y ' : ''"
                style="overflow-y: scroll; overflow-wrap: anywhere"
              >
                <tr v-for="shareHolder in entity.shareHolders">
                  <td e2e-data="entityShareHolderName">{{ formatName(shareHolder) }}</td>
                  <td e2e-data="entityShareHolderPosition">{{ shareHolder.position }}</td>
                  <td e2e-data="entityShareHolderDateOfBirth">{{ shareHolder.dateofbirth }}</td>
                  <td e2e-data="entityShareHolderPlaceOfBirth">{{ shareHolder.placeofbirth }}</td>
                </tr>
              </tbody>
              <tbody v-else class="text-center my-3">
                <tr>
                  <td>{{ $t('entity.information.directorList.empty') }}</td>
                </tr>
              </tbody>
            </table>
          </BlockContent>

          <!-- Liste des bénéficiaires effectifs -->
          <BlockContent
            v-if="entity.beneficialOwners"
            title-t="entity.information.beneficialOwners.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            bloc-content-class="scrollbar-x"
          >
            <table class="table table-striped table-hover fs-16 w-100">
              <thead>
                <tr>
                  <th class="font-weight-bold col-4">
                    {{ $t('entity.information.beneficialOwners.beneficialOwner') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.beneficialOwners.totalShare') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.beneficialOwners.birthday') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.beneficialOwners.placeOfBirth') }}
                  </th>
                  <th class="font-weight-bold col-2">
                    {{ $t('entity.information.beneficialOwners.nationality') }}
                  </th>
                </tr>
              </thead>

              <tbody
                v-if="entity.beneficialOwners.length > 0"
                :class="entity.beneficialOwners.length > 3 ? 'scrollbar-y ' : ''"
                style="overflow-y: scroll; overflow-wrap: anywhere"
              >
                <tr v-for="beneficialOwner in entity.beneficialOwners">
                  <td e2e-data="entityBeneficialOwnerName">{{ formatName(beneficialOwner) }}</td>
                  <td e2e-data="entityBeneficialOwnerTotalShare">{{ beneficialOwner.totalshare }}</td>
                  <td e2e-data="entityBeneficialOwnerDateOfBirth">{{ beneficialOwner.dateofbirth }}</td>
                  <td e2e-data="entityBeneficialOwnerPlaceOfBirth">{{ beneficialOwner.placeofbirth }}</td>
                  <td e2e-data="entityBeneficialOwnerNationality">{{ beneficialOwner.nationality }}</td>
                </tr>
              </tbody>
              <tbody v-else class="text-center my-3">
                <tr>
                  <td colspan="5">{{ $t('entity.information.beneficialOwners.empty') }}</td>
                </tr>
              </tbody>
            </table>
          </BlockContent>
        </div>
        <div class="col-md-6">
          <!-- Exposition -->
          <BlockContent
            title-t="entity.information.exposition.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
          >
            <div class="table-responsive">
              <table class="table w-100 table-borderless align-middle">
                <tbody>
                  <tr>
                    <td class="title text-dark align-middle fs-16" style="min-width: 0">
                      <b>{{ $t('entity.information.exposition.outstanding') }}</b>
                    </td>
                    <td class="text-right">
                      <span
                        class="badge badge-pill fs-14 ml-1 badge-secondary mr-2"
                        style="min-width: 10%"
                        e2e-data="entityOutstandingNumber"
                      >
                        {{ entity.outstanding ? entity.outstanding.number : 0 }}
                      </span>
                    </td>
                    <td class="text-right">
                      <span
                        class="badge badge-pill fs-14 ml-1 badge-secondary"
                        style="min-width: 20%"
                        e2e-data="entityOutstandingAmount"
                      >
                        {{
                          entity.outstanding
                            ? entity.outstanding.amount > 0
                              ? $n(Math.round(entity.outstanding.amount / 1000)) + ' K'
                              : 0
                            : 0
                        }}€
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td class="title text-dark align-middle fs-16" style="min-width: 0">
                      <b>{{ $t('entity.information.exposition.availableDemands') }}</b>
                    </td>
                    <td class="text-right">
                      <span
                        class="badge badge-pill fs-14 ml-1 badge-secondary mr-2"
                        style="min-width: 10%"
                        e2e-data="entityDemandsExpoNumber"
                      >
                        {{ entity.demandsExpo ? entity.demandsExpo.number : 0 }}
                      </span>
                    </td>
                    <td class="text-right">
                      <span
                        class="badge badge-pill fs-14 ml-1 badge-secondary"
                        style="min-width: 20%"
                        e2e-data="entityDemandsExpoAmount"
                      >
                        {{
                          entity.demandsExpo
                            ? entity.demandsExpo.amount > 0
                              ? $n(Math.round(entity.demandsExpo.amount / 1000)) + ' K'
                              : 0
                            : 0
                        }}€
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BlockContent>

          <!-- Score -->
          <BlockContent
            title-t="entity.information.score.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
          >
            <Scoring
              v-if="scores"
              v-for="provider in providers"
              :key="provider"
              :provider="provider"
              :scores="scores"
              :credit-limit="getCreditLimit(provider)"
            />
          </BlockContent>

          <!-- Commentaires -->
          <BlockContent
            title-t="entity.information.comments"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            :bloc-content-class="entity.scoreInfos ? scrollComment(entity.scoreInfos) : ''"
          >
            <div class="row">
              <div class="col-md-3">
                <b>{{ $t('entity.information.provider.cs') }}</b>
              </div>
              <div class="col-md-9" e2e-data="entityCreditSafeComment">
                {{ entity.scoreInfos ? entity.scoreInfos.creditsafe.comment : '-' }}
              </div>
            </div>
            <div class="row mt-1">
              <div class="col-md-3">
                <b>{{ $t('entity.information.provider.eli') }}</b>
              </div>
              <div class="col-md-9" e2e-data="entityEllisphereComment">
                {{ entity.scoreInfos ? entity.scoreInfos.ellisphere.comment : '-' }}
              </div>
            </div>
          </BlockContent>

          <!-- Score historique -->
          <BlockContent
            title-t="entity.information.scoreHistory"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            :bloc-content-class="scores.length >= 4 ? 'scrollbar-y' : ''"
          >
            <table class="table table-striped w-100 mb-0 fs-16">
              <thead>
                <tr>
                  <th class="font-weight-bold col-2">{{ $t('entity.score.date') }}</th>
                  <th class="font-weight-bold col-2">{{ $t('entity.score.score') }}</th>
                  <th class="font-weight-bold col-8">{{ $t('entity.score.comment') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(scores, provider) in scoresGroupedByProvider">
                  <tr class="table-active">
                    <td colspan="3">
                      <strong>{{ provider }}</strong>
                    </td>
                  </tr>
                  <tr v-for="(score, index) in scores" :key="index">
                    <td e2e-data="scoreDate">{{ $root.setDate(score.scoreAt) }}</td>
                    <td>
                      <span class="badge badge-secondary" e2e-data="scoreNumber">{{ formatScore(score) }}</span>
                    </td>
                    <td e2e-data="scoreComment">{{ scoreComment(score) }}</td>
                  </tr>
                </template>
                <template v-if="scores?.length === 0">
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('entity.information.score.empty') }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </BlockContent>

          <!-- Jugements -->
          <BlockContent
            title-t="entity.information.judgements.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
          >
            <div class="row my-3">
              <div class="col-md-6">
                <b>{{ $t('entity.information.judgements.monitoredAt') }}</b>
              </div>
              <div class="col-md-6" e2e-data="entityMonitoredAt">
                {{ entity.monitoredAt ? setDate(entity.monitoredAt) : '' }}
              </div>
            </div>
            <div class="row my-3">
              <div class="col-md-6">
                <b>{{ $t('entity.information.judgements.nbJudgements') }}</b>
              </div>
              <div class="col-md-6" e2e-data="entityNbJudgements">
                {{ entity.nbJudgements }}
              </div>
            </div>
            <div class="row my-3">
              <div class="col-md-6">
                <b>{{ $t('entity.information.judgements.judgementTotal') }}</b>
              </div>
              <div class="col-md-6" e2e-data="entityJudgementTotal">
                {{ entity.judgementTotal ? $n(Number(entity.judgementTotal)) : 0 }}
              </div>
            </div>
            <div class="row my-3">
              <div class="col-md-6">
                <b>{{ $t('entity.information.judgements.judgementDue') }}</b>
              </div>
              <div class="col-md-6" e2e-data="entityJudgementDue">
                {{ entity.judgementDue ? $n(Number(entity.judgementDue)) : 0 }}
              </div>
            </div>
          </BlockContent>

          <!-- Événements -->
          <BlockContent
            v-if="entity.financialEvents"
            title-t="entity.information.events.title"
            title-level="5"
            color="primary"
            inner-bloc-class="border-contract-yellow"
            bloc-content-class="scrollbar-x-mobile"
          >
            <table class="table table-striped table-hover fs-16">
              <thead style="display: block">
                <tr style="display: flex">
                  <th class="font-weight-bold" style="width: 15%">{{ $t('entity.information.events.createdAt') }}</th>
                  <th class="font-weight-bold" style="width: 15%">{{ $t('entity.information.events.occurredAt') }}</th>
                  <th class="font-weight-bold" style="width: 15%">{{ $t('entity.information.events.code') }}</th>
                  <th class="font-weight-bold" style="width: 40%">{{ $t('entity.information.events.event') }}</th>
                  <th class="font-weight-bold" style="width: 15%">{{ $t('entity.information.events.source') }}</th>
                </tr>
              </thead>
              <tbody
                v-if="entity?.financialEvents.length > 0"
                :class="(entity.financialEvents.length > 8 ? 'scrollbar-y ' : '') + 'bloc-entity__bloc'"
                style="display: block; overflow-y: scroll"
              >
                <tr v-for="event in entity.financialEvents" style="display: flex">
                  <td style="width: 15%" e2e-data="eventCreatedAt">
                    {{ event.createdAt ? setDate(event.createdAt, 'date') : '' }}
                  </td>
                  <td style="width: 15%" e2e-data="eventOccuredAt">
                    {{ event.occurredAt ? setDate(event.occurredAt, 'date') : '' }}
                  </td>
                  <td style="width: 15%" e2e-data="eventRuleCode">{{ event.ruleCode }}</td>
                  <td style="width: 40%" e2e-data="eventSummary">{{ event.summary }}</td>
                  <td style="width: 15%" e2e-data="eventSource">{{ event.source }}</td>
                </tr>
              </tbody>
              <tbody v-else class="text-center my-3">
                <tr>
                  <td colspan="5">{{ $t('entity.information.events.empty') }}</td>
                </tr>
              </tbody>
            </table>
          </BlockContent>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="row">
        <div class="col-12">
          <BlockContent inner-bloc-class="border-contract-yellow">
            <div class="row">
              <PlaceholderContent v-for="contentIndex in 2" :key="contentIndex" :rows="6" class="col col-md-6" />
            </div>
          </BlockContent>
        </div>
        <div v-for="colIndex in 2" :key="colIndex" class="col col-md-6 flex-fill">
          <BlockContent v-for="blockIndex in 3" :key="blockIndex" inner-bloc-class="border-contract-yellow">
            <PlaceholderContent :rows="2" />
          </BlockContent>
        </div>
      </div>
    </div>
  </div>
</template>
